import { useQuery } from "@tanstack/react-query"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconLinkButton } from "@tokenterminal/core/ui/button/IconLinkButton"
import { Avatar, type AvatarProps } from "@tokenterminal/core/ui/icon/Avatar"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { useChainsList } from "modules/projects/hooks/useChainsList"
import { useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { useTRPC } from "utils/trpc"
import { ChainsDeployedOnModal } from "./ChainsDeployedOnModal"

export const ProjectLogo = ({
  logoUrl,
  projectName,
  className,
  ...props
}: Omit<AvatarProps, "src" | "label"> & {
  logoUrl: string
  projectName: string
  // AvatarProps is somewhat of a discriminated union, so we specify the types
  // we definitely don't expect
  initials?: never
  icon?: never
}) => {
  return (
    <Avatar
      src={`https://static1.tokenterminal.com/${logoUrl}`}
      className={twMerge(
        "rounded-full overflow-hidden flex-shrink-0",
        className
      )}
      label={`${projectName} logo`}
      {...props}
    />
  )
}

const makeMaskImage = (width: number, overlap: number, border: number) => {
  const radius = width / 2 + border
  const cutoutX = width + (width / 2 - overlap)
  return `radial-gradient(circle at ${cutoutX}px center, transparent ${radius}px, black ${radius}px)`
}

export const LogoStack = ({
  projectLogos,
  size = 16,
  overlap = 4,
  border = 1,
  className,
}: {
  projectLogos: {
    logoUrl: string
    projectName: string
  }[]
  size?: number
  overlap?: number
  border?: number
  className?: string
}) => {
  return (
    <div className={twMerge("flex items-center", className)}>
      {projectLogos.map(({ logoUrl, projectName }, index) => {
        const isNotLast = index < projectLogos.length - 1
        const isNotFirst = index > 0

        return (
          <ProjectLogo
            key={logoUrl}
            logoUrl={logoUrl}
            projectName={projectName}
            className="w-[var(--size)] h-[var(--size)]"
            style={
              {
                "--size": `${size}px`,
                marginLeft: isNotFirst ? `${-overlap}px` : "0px",
                maskImage: isNotLast
                  ? makeMaskImage(size, overlap, border)
                  : undefined,
              } as React.CSSProperties
            }
          />
        )
      })}
    </div>
  )
}

const ChainsDeployedOnLinkOrModal = ({
  chainIds,
  projectSlug,
  shouldFilterSelf = false,
}: {
  chainIds: string[]
  projectSlug: string
  shouldFilterSelf?: boolean
}) => {
  const [deployedOnModalProjectSlug, setDeployedOnModalProjectSlug] = useState<
    string | null
  >(null)
  const trpc = useTRPC()
  const { data: project } = useQuery(
    trpc.projects.getProject.queryOptions({
      slug: projectSlug,
    })
  )
  const { data: { data: chainsMetadata = [] } = {} } = useChainsList()
  const chainsById = useMemo(
    () => toDictionary(chainsMetadata, "chain_id"),
    [chainsMetadata]
  )

  const chains = useMemo(() => {
    return chainIds
      .map((chainId) => chainsById.get(chainId))
      .filter((chain) => chain !== undefined)
      .filter((chain) => {
        if (shouldFilterSelf) {
          return chain?.chain_id !== project?.data_id
        }
        return true
      })
  }, [chainIds, chainsById, project, shouldFilterSelf])

  if (chains.length === 0) {
    return null
  } else if (chains.length === 1) {
    const chain = chains[0]

    return (
      <IconLinkButton
        href={`/projects/${chain.project_slug}`}
        size="small"
        variant="ghost"
        className="!p-1 !w-auto !h-auto -m-1"
        tabIndex={0}
        label={chain?.name}
        icon={() => (
          <ProjectLogo
            key={chain?.chain_id}
            logoUrl={chain?.logo}
            projectName={chain?.name}
            className="w-4 h-4"
          />
        )}
      />
    )
  }

  return (
    <>
      <Tooltip content="View chains deployed on">
        <Button
          variant="ghost"
          size="small"
          className="!p-1 !h-auto -m-1 !gap-1"
          onPress={() => setDeployedOnModalProjectSlug(projectSlug)}
        >
          <LogoStack
            projectLogos={chains
              .slice(0, 3)
              .map((chain) => ({
                logoUrl: chain.logo,
                projectName: chain.name,
              }))
              .filter((chain) => chain.logoUrl !== undefined)}
          />
          {chainIds.length > 3 && (
            <span className="text-fg-secondary font-normal text-sm leading-none mr-1">
              +{chainIds.length - 3}
            </span>
          )}
        </Button>
      </Tooltip>
      {deployedOnModalProjectSlug && (
        <ChainsDeployedOnModal
          projectSlug={deployedOnModalProjectSlug}
          handleClose={() => setDeployedOnModalProjectSlug(null)}
          metricId={null}
          shouldFilterSelf={false}
        />
      )}
    </>
  )
}

export default ChainsDeployedOnLinkOrModal
